import _ from 'lodash';

export const allAllowed = ['SERVICE-MANAGER', 'SERVICE-STAFF', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF', 'SHOP-MANAGER', 'SHOP-STAFF'];
export const organizationAllowed = ['SERVICE-MANAGER', 'SERVICE-STAFF', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF'];
export const serviceAllowed = ['SERVICE-MANAGER', 'SERVICE-STAFF'];
export const serviceManagerAllowed = ['SERVICE-MANAGER'];
export const managerAllowed = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER'];
// サービス権限 (SERVICE-*) + 発行体組織マネージャー (ORGANIZATION-MANAGER)
export const serviceAndOrgManagerAllowed = ['SERVICE-MANAGER', 'SERVICE-STAFF', 'ORGANIZATION-MANAGER'];
export const organizationManagerAllowed = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER'];
export const organizationManagerOnlyAllowed = ['ORGANIZATION-MANAGER'];
export const individualNumberIdentificationAllowed = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF'];
export const nonServiceRoles = ['ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF', 'SHOP-MANAGER', 'SHOP-STAFF'];
export const shopRoles = ['SHOP-MANAGER', 'SHOP-STAFF'];
export const nonShopRoles = ['SERVICE-MANAGER', 'SERVICE-STAFF', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF'];
export const refundAllowed = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER', 'SHOP-MANAGER', 'SHOP-STAFF'];
export const canCreateShop = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF'];
export const canCreateDevice = ['SERVICE-MANAGER', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF'];
export const canShowShop = allAllowed;
export const canShowDevice = allAllowed;
export const canInvalidatePokeregi = ['SERVICE-MANAGER', 'SERVICE-STAFF', 'ORGANIZATION-MANAGER', 'ORGANIZATION-STAFF', 'SHOP-MANAGER'];
export const thirdPartyIssuerAllowed = ['third-party'];
export const allIssuerAllowed = ['third-party', 'own'];

// /organizations/* 系ルートで使用。third-party マネー選択中の SERVICE ユーザー、
// または該当マネーの発行体組織ユーザー (Shop ユーザーを除く) を許可する。
export const canManageThirdPartyIssuerOrg = ({ user, selectedPrivateMoney }) =>
  !!selectedPrivateMoney?.id &&
  selectedPrivateMoney.type === 'third-party' &&
  (user.isServiceUser?.() ||
    (user.isIssuerOf?.(selectedPrivateMoney.id) && !user.isShopUser?.()));

// /shops/applications 系ルートで使用。third-party マネー選択中の SERVICE / 組織ユーザーを許可する
// (加盟組織と発行体組織のどちらも閲覧可)。
export const canManageThirdPartyShopApplications = ({ user, selectedPrivateMoney }) =>
  !!selectedPrivateMoney &&
  selectedPrivateMoney.type === 'third-party' &&
  (user.isServiceUser?.() || user.isOrgUser?.());

// 精算データの精算済化・相殺操作は「発行体組織のマネージャー権限」のみ許可する。
// 一覧の一括精算 / 行単位の精算ボタン / 詳細の相殺操作で共通の認可ゲートとして使う
// （最終防衛は BE 側。FE は導線の出し分け用）。
export const canSettleClearing = (user) =>
  !!user?.role &&
  !!user.organization?.issuer_type?.length &&
  organizationManagerAllowed.includes(user.role);
// 加盟店 申込→審査→承認 フローの表示可否の基底述語。選択中マネーの「発行体組織」の
// display feature フラグ (display_features_by_organization[money.organization.code].merchant_application)
// に追従する。未取得・未設定は false (=非表示)。発行体/加盟事業者いずれのログインでも、
// 選択中マネー起点で同じ値になる。
// 判定はユーザ(AdminUser)が持つ map から解決するため、選択中マネーと user の両方を受ける。
// reducers/user-private-money.ts の selectShowMerchantApplication からも参照される。
export const merchantApplicationEnabledOf = (selectedPrivateMoney, user) =>
  user?.isDisplayFeatureEnabled?.(selectedPrivateMoney, 'merchant_application') === true;

// ルートガード ctx ({ user, selectedPrivateMoney }) 版。
export const isMerchantApplicationEnabled = ({ user, selectedPrivateMoney }) =>
  merchantApplicationEnabledOf(selectedPrivateMoney, user);

// 申込フロー系ルートのガード。既存の権限判定に「発行体が申込フローを利用中」条件を AND する。
export const requireMerchantApplication = (fn) => (ctx) =>
  isMerchantApplicationEnabled(ctx) && fn(ctx);

const canEditTransaction = (user, transaction) => {
  const isServiceUser = serviceAllowed.includes(user.role);
  const isOwnOrganizationTransaction = isServiceUser || (transaction.organization_code === user.organization.code && user.role !== 'ORGANIZATION-STAFF');
  const isManageableTransaction = !shopRoles.includes(user.role) || _.find(user.shops, { id: transaction.shop_id });
  return isOwnOrganizationTransaction && isManageableTransaction;
};

export const authorityRules = {
  issuer: {
    'personalInfo:read': user => organizationAllowed.includes(user.role),
    'transaction:edit': canEditTransaction,
    'allOrganizations:read': user => serviceAllowed.includes(user.role),
    'QRDesign:read': user => allAllowed.includes(user.role),
    'QRDesign:write': user => organizationAllowed.includes(user.role),
    'check:read': user => organizationAllowed.includes(user.role),
    'check:write': user => organizationManagerAllowed.includes(user.role),
    'memberBill:select': user => organizationAllowed.includes(user.role) && user.organization.issuer_type.includes('third-party'),
    'privateMoney:write': user => organizationAllowed.includes(user.role),
    'individualNumberIdentification:write': user => individualNumberIdentificationAllowed.includes(user.role),
  },
  member: {
    'personalInfo:read': user => serviceAllowed.includes(user.role),
    'transaction:edit': canEditTransaction,
    'allOrganizations:read': user => serviceAllowed.includes(user.role),
    'QRDesign:read': user => allAllowed.includes(user.role),
    'QRDesign:write': user => serviceAllowed.includes(user.role),
    'check:read': user => serviceAllowed.includes(user.role),
    'check:write': user => serviceAllowed.includes(user.role),
    'memberBill:select': user => serviceAllowed.includes(user.role),
    'privateMoney:write': user => false,
    'individualNumberIdentification:write': user => serviceManagerAllowed.includes(user.role),
  },
};
